import {
  FC,
  MouseEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDays } from "lucide-react";
import type { Task } from "@myapp/contract";
import Modal from "../../../shared/ui/overlay/Modal/Modal";
import { TechnicianFeedbackSummary } from "../../../shared/ui";
import { Button } from "../../../shared/ui/controls/Button/Button";
import DeleteItemDialog from "../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { DiscardChangesDialog } from "../../../shared/ui/feedback/DiscardChangesDialog/DiscardChangesDialog";
import { ScheduleConflictDialogs } from "../../../shared/ui/feedback/ScheduleConflictDialogs/ScheduleConflictDialogs";
import { useDeleteDialog } from "../../../shared/hooks/useDeleteDialog";
import { useScheduleConflictFlow } from "../../../shared/hooks/useScheduleConflictFlow";
import { sameSlot, type ScheduleOverrides, type ScheduleSlot } from "../../../shared/lib/schedule";
import { routes } from "../../../shared/config/routes";
import { getErrorMessage } from "../../../shared/helpers/getErrorMessage";
import {
  taskApi,
  useCreateTask,
  useDeleteTask,
  useScheduleTask,
  useTaskDetail,
  useUnscheduleTask,
  useUpdateTask,
} from "../../../entities/task";
import {
  TaskFormValues,
  applySlot,
  fromTask,
  getDefaultValues,
  isScheduled,
  taskFormSchema,
  taskHasActuals,
  toCreateDto,
  toScheduleDto,
  toSlot,
  toUpdateDto,
} from "../model/taskForm.schema";
import { useUpsertTask } from "../model/useUpsertTaskParams";
import { TaskFormFields } from "./TaskFormFields";

/**
 * Create / edit a task of either type, driven entirely by URL state.
 *
 * ONE ROUTE FOR BOTH MODES OF CREATE: `POST /tasks` makes a single task when `recurrence` is
 * absent and a whole series when it is present — the backend expands the rule, reserves every
 * occurrence and mints the `seriesId`. One conflict rejects the whole series and writes nothing,
 * so there is no partial-success path here.
 *
 * THE ONE PLACE THIS NEEDS TWO CALLS: `PATCH /tasks/:id` silently ignores schedule fields on a
 * task that has no entry yet (`UpdateTaskUseCase` — "scheduling one is ScheduleTaskUseCase's
 * job"). Giving an unscheduled task its first termin therefore goes through
 * `PATCH /tasks/:id/schedule` afterwards, in that order: the update assigns the Handwerker, and
 * the reservation is then made in that Handwerker's name.
 *
 * CONFLICTS: every route here answers 409 in the same three flavours as the appointment routes,
 * and `useScheduleConflictFlow` handles all three identically — see
 * `features/appointment-upsert/ui/UpsertAppointmentModal.tsx` for what each one means.
 *
 * KALENDER: the header carries a link onto the disposition board, the twin of the appointment
 * modal's. One difference: the appointment modal offers the link only where `workOrderId` says the
 * page owns the Auftrag; a task has no such prop, so the tell is `onSlotChange` — the board is the
 * one mounting point that listens for the slot, and on it you are already there. Every task type
 * gets the link, because every task type can hold a Termin.
 *
 * Where it goes follows the same `schedule == null` question the board itself asks: an unscheduled
 * task is a first placement (`planTask`), one that already holds a slot is a move
 * (`rescheduleTask`, which carries the Termin's own day so the board opens where the Aufgabe is).
 * Create has neither id nor day — `planTask()` plans a fresh one. The type is deliberately not
 * carried along: the navigation abandons the whole form, and the type is one of the things typed
 * into it. The board's confirm therefore reopens create at the `GENERAL_TASK` default, the same
 * way it arrives with no title and no description.
 *
 * That abandonment is what `DiscardChangesDialog` warns about before a dirty form leaves.
 *
 * WHY A RETRY MAY RE-RUN THE WHOLE SUBMIT, two calls and all: the two calls can never both
 * conflict. When the task already owns an entry, only the PATCH validates timing; when it does
 * not, that PATCH skips scheduling validation entirely and only `/schedule` can reject. The
 * DELETE that unschedules checks nothing. Re-sending the PATCH is idempotent — attachments are
 * replaced, not appended — and a rejected reservation leaves `detail.schedule` null, so
 * `hasSchedule` cannot flip between attempts.
 */

/**
 * A slot chosen somewhere else — today the disposition board.
 *
 * Kept as an alias so the name still reads at the call sites; the shape is the shared
 * `ScheduleSlot`, which is also what the board draws and what `onSlotChange` reports back.
 */
export type UpsertTaskDefaultSlot = ScheduleSlot;

interface Props {
  /** Prefills the type on create when the URL carries none. */
  defaultTaskType?: "ERRANDS" | "GENERAL_TASK";
  /**
   * Handwerker and Termin from outside the form. **Controlled, not just a default**: it seeds the
   * form when the modal opens and every later change to it is written into the five scheduling
   * fields while the modal stays open — the board→form half of the live preview, so dragging the
   * block on the calendar retypes the form.
   *
   * Applied in **edit** mode too, unlike the appointment modal: an unscheduled task is given its
   * first Termin through this form, so the picked slot has to survive `fromTask`, which would
   * otherwise reset both fields to the task's own empty schedule.
   */
  defaultSlot?: UpsertTaskDefaultSlot;
  /**
   * The form→board half: called with the five scheduling fields whenever they change, with `null`
   * while the modal is closed, and never with a half-typed field — the calendar keeps drawing the
   * last complete position instead of flickering between two keystrokes.
   *
   * A task with no Termin at all reports `null` too — an ordinary state for any type, not an
   * error. There is simply nothing to draw until a Handwerker and a start are both there.
   *
   * Doubles as the "you are on the board" tell for the Kalender link in the header — see the
   * KALENDER note above.
   */
  onSlotChange?: (slot: ScheduleSlot | null) => void;
  /** `side` docks the form beside the calendar and leaves it usable. Default: a centred dialog. */
  placement?: "center" | "side";
  onSuccess?: (task: Task) => void;
}

const FormSkeleton: FC = () => (
  <div className="flex w-full animate-pulse flex-col gap-6">
    {[0, 1, 2].map((section) => (
      <div key={section} className="flex flex-col gap-4">
        <div className="h-4 w-28 rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <div className="h-[60px] rounded-[18px] bg-slate-100" />
          <div className="h-[60px] rounded-[18px] bg-slate-100" />
        </div>
      </div>
    ))}
  </div>
);

export const UpsertTaskModal: FC<Props> = ({
  defaultTaskType,
  defaultSlot,
  onSlotChange,
  placement = "center",
  onSuccess,
}) => {
  const { isOpen, mode, taskId, taskType: taskTypeFromUrl, close } = useUpsertTask();

  const navigate = useNavigate();

  const isEdit = mode === "edit";
  const createTaskType = taskTypeFromUrl ?? defaultTaskType ?? "GENERAL_TASK";

  const {
    data: detail,
    isFetching: isDetailFetching,
    isError: isDetailError,
  } = useTaskDetail(isEdit && taskId != null ? taskId : undefined);

  const createTask = useCreateTask();
  const updateTask = useUpdateTask(taskId ?? 0);
  const scheduleTask = useScheduleTask(taskId ?? 0);
  const unscheduleTask = useUnscheduleTask(taskId ?? 0);
  const deleteTask = useDeleteTask();
  const isPending =
    createTask.isPending ||
    updateTask.isPending ||
    scheduleTask.isPending ||
    unscheduleTask.isPending;

  const [files, setFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const deleteDialog = useDeleteDialog();
  const conflictFlow = useScheduleConflictFlow();
  const { reset: resetConflicts } = conflictFlow;

  const { control, handleSubmit, reset, watch, setValue, getValues, formState } =
    useForm<TaskFormValues>({
      // Input and output types diverge (z.coerce on the durations), which zodResolver's generics
      // cannot express. Same cast as features/appointment-upsert.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: zodResolver(taskFormSchema) as any,
      defaultValues: getDefaultValues(createTaskType),
    });

  /**
   * The seeding slot, read through a ref rather than from the closure — `defaultSlot` changes on
   * every drag of the block on the calendar, and in the dependency array below that would re-`reset`
   * the whole form mid-edit. The live value is applied by the effect after the reset instead.
   *
   * `useLayoutEffect`: layout effects run before passive ones in the same commit, so the ref is
   * already current when the reset reads it. Mirrors `features/appointment-upsert`.
   */
  const slotRef = useRef(defaultSlot);
  useLayoutEffect(() => {
    slotRef.current = defaultSlot;
  }, [defaultSlot]);

  /**
   * What the form currently holds — `edit:7` or `create:GENERAL_TASK` — and therefore whether it may
   * be filled again. **Seeded exactly once per opening, and only from a settled query.**
   *
   * `detail` is a React Query object and can arrive twice: an invalidated task is served from cache
   * first (no `isPending`, so no skeleton) and refetched behind it. Two arrivals meant two resets
   * with two different times, and since this form publishes what it holds to the calendar
   * (`onSlotChange`), the block jumped to the stale slot and back. The same guard keeps a later
   * background refetch from overwriting what the user has typed. Mirrors
   * `features/appointment-upsert`.
   */
  const seedKey = isEdit
    ? taskId != null
      ? `edit:${taskId}`
      : null
    : // Create has no query of its own; the type can still change from outside, and must land.
      `create:${createTaskType}`;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const isSeeded = seededFor != null && seededFor === seedKey;

  // The Modal stays mounted and only toggles a CSS class, so without this the form would still
  // hold the previously edited task when it reopens.
  useEffect(() => {
    // Cleared on the way *out* too: the fields keep the closed form's values, and a `seededFor` that
    // outlived the closing would let them reach the calendar for the frame before the reopen
    // re-seeds. Mirrors `features/appointment-upsert`.
    if (!isOpen) {
      setSeededFor(null);
      return;
    }
    setFiles([]);
    setSubmitError(null);
    setPendingExit(null);
    resetConflicts();
    setSeededFor(null);
  }, [isOpen, resetConflicts]);

  useEffect(() => {
    if (!isOpen || isSeeded || seedKey == null) return;

    if (isEdit) {
      // Still in flight — including a background refetch over a stale cache, the case that used to
      // seed the previous termin. `FormSkeleton` covers the wait.
      if (isDetailFetching) return;
      // A failed query is settled too: it will not come back on its own, and a skeleton that never
      // goes away is worse than the empty form it falls through to.
      if (!detail && !isDetailError) return;

      // `applySlot`, not a spread: the slot's keys are the *planned* field names, and on a completed
      // Aufgabe the picked position belongs on the Ist-Zeiten — the ones the calendar draws.
      if (detail) reset(applySlot(fromTask(detail), slotRef.current));
      setSeededFor(seedKey);
      return;
    }

    reset({ ...getDefaultValues(createTaskType), ...slotRef.current });
    setSeededFor(seedKey);
  }, [
    isOpen,
    isSeeded,
    seedKey,
    isEdit,
    detail,
    isDetailFetching,
    isDetailError,
    createTaskType,
    reset,
  ]);

  const existingAttachments = watch("attachments");

  /**
   * board → form: a slot that changes *while* the modal is open, i.e. the block being dragged on the
   * calendar. Field by field and only where the value differs, never `reset` — the rest of the form
   * is the user's, and `sameSlot` stops the echo of what this form itself just reported.
   *
   * **Which four fields** is the status's call, exactly as in `toSlot`: a completed Aufgabe is drawn
   * from its Ist-Zeiten, so that is what a drag on the board retypes. Writing the planned ones
   * instead would move a block nobody can see and leave the drawn one behind. The status is read off
   * `getValues` rather than watched, so a status change does not re-run this effect — the flip is
   * the next effect's business.
   */
  useEffect(() => {
    if (!isOpen || !defaultSlot || !isSeeded) return;
    const values = getValues();
    if (sameSlot(toSlot(values), defaultSlot)) return;

    const options = { shouldDirty: true } as const;
    setValue("assignedTechnicianId", defaultSlot.assignedTechnicianId, options);

    if (taskHasActuals(values.status)) {
      setValue("actualStart", defaultSlot.plannedStart, options);
      setValue("actualTravelDuration", defaultSlot.plannedTravelDuration, options);
      setValue("actualWorkDuration", defaultSlot.plannedWorkDuration, options);
      setValue("actualDepartureDuration", defaultSlot.plannedDepartureDuration, options);
      return;
    }

    setValue("plannedStart", defaultSlot.plannedStart, options);
    setValue("plannedTravelDuration", defaultSlot.plannedTravelDuration, options);
    setValue("plannedWorkDuration", defaultSlot.plannedWorkDuration, options);
    setValue("plannedDepartureDuration", defaultSlot.plannedDepartureDuration, options);
  }, [isOpen, isSeeded, defaultSlot, getValues, setValue]);

  /**
   * form → board. `useWatch` on exactly the fields the calendar draws — `watch()` would re-render
   * this component on every character typed into Beschreibung.
   */
  const [
    watchedTechnicianId,
    watchedStart,
    watchedTravel,
    watchedWork,
    watchedDeparture,
    watchedStatus,
    watchedActualStart,
    watchedActualTravel,
    watchedActualWork,
    watchedActualDeparture,
  ] = useWatch({
    control,
    name: [
      "assignedTechnicianId",
      "plannedStart",
      "plannedTravelDuration",
      "plannedWorkDuration",
      "plannedDepartureDuration",
      // The status picks which of the two sets below the calendar is shown, so it has to be watched
      // as closely as the values themselves — see `toSlot`.
      "status",
      "actualStart",
      "actualTravelDuration",
      "actualWorkDuration",
      "actualDepartureDuration",
    ],
  });

  // Already subscribed to above — the recurrence preview reads the same value.
  const plannedStart = watchedStart ?? "";

  const liveSlot = useMemo(
    () =>
      toSlot({
        assignedTechnicianId: watchedTechnicianId ?? null,
        status: watchedStatus,
        plannedStart: watchedStart ?? "",
        plannedTravelDuration: watchedTravel,
        plannedWorkDuration: watchedWork,
        plannedDepartureDuration: watchedDeparture,
        actualStart: watchedActualStart ?? "",
        actualTravelDuration: watchedActualTravel,
        actualWorkDuration: watchedActualWork,
        actualDepartureDuration: watchedActualDeparture,
      }),
    [
      watchedTechnicianId,
      watchedStatus,
      watchedStart,
      watchedTravel,
      watchedWork,
      watchedDeparture,
      watchedActualStart,
      watchedActualTravel,
      watchedActualWork,
      watchedActualDeparture,
    ],
  );

  /**
   * Plan → Ist, once, when the status is switched to "Abgeschlossen".
   *
   * The moment `taskHasActuals` turns true the form changes which four fields it publishes to the
   * calendar. Without this the block would jump to the stored Ist position — usually the *planned*
   * one, since `fromTask` falls back to it — and a move the user had just made by dragging would
   * appear to be thrown away. Copying the Plan values across keeps the block exactly where it is,
   * and opens the Ist-Zeiten showing what was planned: the right starting point for a correction.
   *
   * **Adopt on the first pass, derive only on a change.** A baseline of `false` would fire this on
   * every completed Aufgabe the moment it loads and overwrite the very Ist-Zeiten the Handwerker
   * reported. `null` means "not yet baselined", which is why the ref is cleared whenever the form is
   * unseeded rather than just on close. Mirrors `features/appointment-upsert`.
   */
  const wasDoneRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isOpen || !isSeeded) {
      wasDoneRef.current = null;
      return;
    }

    const values = getValues();
    const isDone = taskHasActuals(values.status);

    if (wasDoneRef.current === null || wasDoneRef.current === isDone) {
      wasDoneRef.current = isDone;
      return;
    }

    wasDoneRef.current = isDone;
    // Leaving the completed state changes nothing: the Plan-Zeiten were never touched while the Ist
    // ones were being edited, so the form simply goes back to publishing them.
    if (!isDone) return;

    const options = { shouldDirty: true } as const;
    setValue("actualStart", values.plannedStart, options);
    setValue("actualTravelDuration", values.plannedTravelDuration, options);
    setValue("actualWorkDuration", values.plannedWorkDuration, options);
    setValue("actualDepartureDuration", values.plannedDepartureDuration, options);
  }, [isOpen, isSeeded, watchedStatus, getValues, setValue]);

  useEffect(() => {
    if (!onSlotChange) return;
    // Closed: nothing is being planned, so the calendar goes back to what it drew before.
    if (!isOpen) {
      onSlotChange(null);
      return;
    }
    // Nothing is published before the form has been seeded from a settled query — that is what keeps
    // a stale cached task from reaching the calendar.
    if (!isSeeded) return;
    // `null` is an incomplete termin, or none at all. Either way, say nothing and let the last
    // complete position stand.
    if (liveSlot) onSlotChange(liveSlot);
  }, [onSlotChange, isOpen, isSeeded, liveSlot]);

  /**
   * Where "Kalender" goes, or `null` for no link at all.
   *
   * Not offered on the board itself (`onSlotChange` present) — there you are already looking at
   * the calendar. Edit waits for `detail`: it is the only thing that knows whether this Aufgabe
   * already holds a slot, and a move carries that slot's day so the board opens on it rather than
   * on today.
   *
   * Waits for the seeding because the header sits outside `FormSkeleton`: until the reset lands,
   * `detail` may still describe the previously opened task and the link would point at its day.
   * Same reason the appointment modal blanks its subtitle.
   */
  const calendarTo = (() => {
    if (!isSeeded || onSlotChange) return null;
    if (!isEdit) return routes.disposition.planTask();
    if (taskId == null || !detail) return null;
    return detail.schedule
      ? routes.disposition.rescheduleTask(taskId, detail.schedule.plannedWorkStartsAt)
      : routes.disposition.planTask(taskId);
  })();

  /**
   * Every way out of a half-filled form asks first: the Kalender link, which leaves the page and
   * takes the form with it, and plain dismissal — Escape, the backdrop, the X — which used to throw
   * the input away in silence. Mirrors `features/appointment-upsert`.
   */
  const [pendingExit, setPendingExit] = useState<
    { kind: "navigate"; to: string } | { kind: "close" } | null
  >(null);

  /** Modifier-clicks fall through untouched — opening a new tab loses nothing. */
  const guardNav = (to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (formState.isDirty) {
      setPendingExit({ kind: "navigate", to });
      return;
    }
    // Deliberately no `close()` first: it is a nuqs replace-state, and landing after the route
    // change it would rewrite the destination URL and strip the query string the route builder
    // just produced. Navigating to an absolute path drops the `upsert-task*` params anyway.
    navigate(to);
  };

  const requestClose = () => {
    if (formState.isDirty) {
      setPendingExit({ kind: "close" });
      return;
    }
    close();
  };

  const removeExistingAttachment = (index: number) =>
    setValue(
      "attachments",
      existingAttachments.filter((_, i) => i !== index),
      { shouldDirty: true },
    );

  /**
   * `values` already carries the uploaded attachments on a retry, so `alreadyUploaded` skips a
   * second round trip for the same files.
   *
   * The overrides are accumulated by `useScheduleConflictFlow` and arrive here already merged —
   * this function only hands them to whichever mapper runs.
   */
  const submit = async (
    values: TaskFormValues,
    {
      overrides = {} as ScheduleOverrides,
      alreadyUploaded = false,
    }: { overrides?: ScheduleOverrides; alreadyUploaded?: boolean } = {},
  ) => {
    setSubmitError(null);
    // Declared outside the try so the retry closure below can resend the exact payload that was
    // rejected — uploads and all.
    let withUploads: TaskFormValues = values;
    try {
      const uploaded =
        files.length && !alreadyUploaded ? (await taskApi.uploadFiles(files)).data : [];
      withUploads = {
        ...values,
        attachments: [...values.attachments, ...uploaded],
      };

      let task: Task;
      if (isEdit) {
        const hasSchedule = detail?.schedule != null;
        task = await updateTask.mutateAsync(
          toUpdateDto(withUploads, {
            hasSeries: detail?.seriesId != null,
            hasSchedule,
            ...overrides,
          }),
        );

        if (hasSchedule) {
          // The form no longer describes a reservation, i.e. the Termin was emptied — an ordinary
          // edit now that it is optional for every type. Omitting the fields above means "leave
          // them alone", so releasing the slot needs its own call.
          //
          // Note `DELETE /tasks/:id/schedule` also clears `seriesId` (see apps/web/CLAUDE.md):
          // emptying the Termin on an occurrence detaches it from its series. Long-standing
          // behaviour, reachable more often now that any task may drop its Termin.
          if (!isScheduled(withUploads)) task = await unscheduleTask.mutateAsync();
        } else {
          // First termin for a task that had none — the PATCH above could not carry it.
          const scheduleDto = toScheduleDto(withUploads, overrides);
          if (scheduleDto) task = await scheduleTask.mutateAsync(scheduleDto);
        }
      } else {
        task = await createTask.mutateAsync(toCreateDto(withUploads, overrides));
      }

      onSuccess?.(task);
      close();
    } catch (error) {
      const handled = conflictFlow.handle(error, {
        overrides,
        attempt: (next) => submit(withUploads, { overrides: next, alreadyUploaded: true }),
      });
      if (!handled) setSubmitError(getErrorMessage(error));
    }
  };

  const onSubmit = handleSubmit((values) => submit(values));

  /**
   * Ties the footer button to the `<form>` below. The footer is a sibling of the modal body, so
   * without the `form` attribute the button sits outside the form and Enter has no submit button
   * to trigger. See `Modal`'s `SubmitButtonConfig.formId`.
   */
  const formId = useId();

  const askDelete = () =>
    deleteDialog.openDelete(
      "Aufgabe wirklich löschen?",
      async ({ forFullSeries }) => {
        await deleteTask.mutateAsync({ id: taskId as number, forFullSeries });
        close();
      },
      // An occurrence and the rest of its rule are different deletions — the dialog asks which.
      {
        isSeries: detail?.seriesId != null,
        description:
          detail?.seriesId != null ? "Diese Aufgabe gehört zu einer Serie." : undefined,
      },
    );

  return (
    <>
      <Modal
        show={isOpen}
        setShow={(next) => {
          if (!next) requestClose();
        }}
        size="wide"
        placement={placement}
        title={isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
        topLeftChildren={
          calendarTo && (
            <Link
              to={calendarTo}
              onClick={guardNav(calendarTo)}
              title={
                isEdit && detail?.schedule
                  ? "Aufgabe im Kalender verschieben"
                  : "Aufgabe im Kalender planen"
              }
              className="flex items-center gap-1.5 text-sm text-[#124093] hover:underline"
            >
              <CalendarDays size={16} aria-hidden="true" />
              Kalender
            </Link>
          )
        }
        actionButtons={
          isEdit ? (
            <Button variant="neutral" onClick={askDelete} disabled={isPending}>
              Löschen
            </Button>
          ) : undefined
        }
        submitButton={{
          formId,
          title: isEdit ? "Speichern" : "Aufgabe anlegen",
          isPending,
          // Behind the skeleton the `<form>` does not exist yet, so `form={formId}` would resolve
          // to nothing and the button would do nothing.
          disabled: isPending || (isEdit && !isSeeded),
        }}
        footerMessage={
          submitError && (
            <p className="text-sm text-red-500" role="alert">
              {submitError}
            </p>
          )
        }
      >
        {isEdit && !isSeeded ? (
          <FormSkeleton />
        ) : (
          // Real submit handler — see the twin comment in `UpsertAppointmentModal`. The footer
          // button is adopted by this form through `form={formId}`, which is what gives the form a
          // submit button and therefore Enter-to-save.
          <div className="flex w-full flex-col gap-6">
            <form id={formId} onSubmit={onSubmit} noValidate className="w-full">
              <fieldset disabled={isPending} className="m-0 w-full border-0 p-0">
                <TaskFormFields
                  control={control}
                  mode={isEdit ? "edit" : "create"}
                  hasSeries={detail?.seriesId != null}
                  plannedStart={plannedStart}
                  files={files}
                  onFilesChange={setFiles}
                  existingAttachments={existingAttachments}
                  onRemoveExistingAttachment={removeExistingAttachment}
                />
              </fieldset>
            </form>
            {/* Read-only, outside the <form> — see the appointment modal. */}
            {isEdit && detail && (
              <TechnicianFeedbackSummary
                comment={detail.technicianComment}
                attachments={detail.technicianAttachments}
              />
            )}
          </div>
        )}
      </Modal>

      <DeleteItemDialog
        deleteModal={deleteDialog}
        singleLabel="Nur diese Aufgabe"
        seriesLabel="Diese und alle folgenden"
      />

      {/* Siblings of the Modal, not children: every modal shares z-index 100, so the one later in
        the DOM wins. */}
      <DiscardChangesDialog
        open={pendingExit != null}
        message={
          pendingExit?.kind === "close"
            ? "Ihre Eingaben in dieser Aufgabe gehen verloren, wenn Sie sie jetzt schließen."
            : "Ihre Eingaben in dieser Aufgabe gehen verloren, wenn Sie die Seite jetzt verlassen."
        }
        onConfirm={() => {
          const exit = pendingExit;
          setPendingExit(null);
          if (exit?.kind === "navigate") navigate(exit.to);
          else if (exit?.kind === "close") close();
        }}
        onCancel={() => setPendingExit(null)}
      />

      <ScheduleConflictDialogs
        flow={conflictFlow}
        isLoading={isPending}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
        conflictTitle="Terminüberschneidung erkannt"
        conflictDescription={
          isEdit
            ? "Der gewählte Zeitraum ist bereits belegt. Möchten Sie die Aufgabe trotzdem speichern? Die betroffenen Einträge werden zur Umplanung markiert."
            : "Der gewählte Zeitraum ist bereits belegt. Möchten Sie die Aufgabe trotzdem anlegen? Die betroffenen Einträge werden zur Umplanung markiert."
        }
        conflictConfirmLabel={isEdit ? "Trotzdem speichern" : "Trotzdem anlegen"}
        workingHoursDescription={
          isEdit
            ? "Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie die Aufgabe trotzdem speichern?"
            : "Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie die Aufgabe trotzdem anlegen?"
        }
        blockersDescription="Die folgenden abgeschlossenen Einträge blockieren diesen Zeitraum. Bitte planen Sie diese zuerst um, bevor Sie die Aufgabe speichern."
      />
    </>
  );
};
